// --------------------------------------------------------------
// Created On: 2023-05-27
// Author: Zachary Thomas
//
// Last Modified: 2026-09-02
// Modified By: Dimitra Weinstein
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect } from "react";
import {
  API,
  MS_PER_MINUTE,
  MS_PER_SECOND,
  MAX_MINUTES_FOR_ACCURATE_READING,
  DATA_STARTING_DATE_OFFSET,
} from "../../constants/miscellaneous";
import { POWER_TO_PANEL_ATTRIBUTE, ENGINE_RUNNING_ATTRIBUTE, RPM_ATTRIBUTE } from "../../constants/attributes";
import { ASSET_DETAILS_MAX_INACTIVE_USER_SECONDS, ASSET_DETAILS_POLLING_DELAY_SECONDS } from "../../constants/polling";
import isValueBooleanString from "../../utilities/isValueBooleanString";
import useApi from "../../hooks/useApi";
import Spinner from "../../components/Spinner/Spinner";
import Error500Page from "../Error500Page/Error500Page";
import CompleteAttributeList from "./CompleteAttributeList/CompleteAttributeList";
import AssetAttributeCard from "./AssetAttributeCard/AssetAttributeCard";
import isoToLocalDate from "../../utilities/time/isoToLocalDate";
import AssetDetails from "../../components/AssetDetails/AssetDetails";
import isoUtcToIsoLocal from "../../utilities/time/isoUtcToIsoLocal";
import DataHistoryModal from "./DataHistoryModal/DataHistoryModal";
import isoAddDays from "../../utilities/time/isoAddDays";
import { useParams } from "react-router-dom";
import useReduxSelector from "../../hooks/useReduxSelector";
import ProductDataVisual from "./ProductDataVisual/ProductDataVisual";
import useLastActive from "../../hooks/useLastActive";
import AssetStatusBannerContainer from "src/components/AssetStatusBannerContainer/AssetStatusBannerContainer";

// Page for viewing attribute data for the current asset.
export default function AssetDataPage(): Component {
  const [initialPageLoading, setInitialPageLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [controllerModel, setControllerModel] = useState<string | null>(null);
  const [deviceId, setDeviceId] = useState<number | null>(null);
  const [deviceType, setDeviceType] = useState<string | null>(null);
  const [deviceTypeName, setDeviceTypeName] = useState<string | null>(null);
  const [deviceIdentifier, setDeviceIdentifier] = useState<string | null>(null);
  const [productType, setProductType] = useState<string>("");
  const [nickname, setNickname] = useState<string>("");
  const [productModel, setProduct] = useState<string>("");
  const [productIdentifier, setProductIdentifier] = useState<string>("");
  const [productManufacturer, setProductManufacturer] = useState<string>("");
  const [isRented, setIsRented] = useState<boolean>(false);
  const [isInNetwork, setIsInNetwork] = useState<boolean>(false);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [attributeMap, setAttributeMap] = useState<AttributesById | null>(null);
  const [cards, setCards] = useState<AssetAttributeCard[]>([]);
  const [highlightAttributes, setHighlightAttributes] = useState<Attribute[]>([]);
  const [lastComm, setLastComm] = useState<string | null>(null);
  const [isMigrating, setIsMigrating] = useState<boolean>(false);
  const [migrationCompleted, setMigrationCompleted] = useState<boolean>(false);
  const [lastConfigAttemptUtc, setLastConfigAttemptUtc] = useState<string | null>(null);
  const [lastUpdateFirmwareAttemptUtc, setLastUpdateFirmwareAttemptUtc] = useState<string | null>(null);
  const [onLatestFirmware, setOnLatestFirmware] = useState<boolean | null>(null);
  const [timerCounter, setTimerCounter] = useState<number>(0);
  const [dataPollCounter, setDataPollCounter] = useState<number>(0);
  const [selectedHistoryAttributeId, setSelectedHistoryAttributeId] = useState<number | null>(null);
  const [selectedHistoryStartingDate, setSelectedHistoryStartingDate] = useState<string | null>(null);
  const [firmwareStatus, setFirmwareStatus] = useState<Status>("SUCCEEDED");
  const [previousFirmwareStatus, setPreviousFirmwareStatus] = useState<Status>("SUCCEEDED");
  const [configurationStatus, setConfigurationStatus] = useState<Status>("SUCCEEDED");
  const [previousConfigurationStatus, setPreviousConfigurationStatus] = useState<Status>("SUCCEEDED");
  const [networkAssets, setNetworkAssets] = useState<NetworkAsset[]>([]);
  const userInactiveSeconds = useLastActive(ASSET_DETAILS_POLLING_DELAY_SECONDS);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const { assetId } = useParams();

  // Get asset data from API.
  useApi(
    () => {
      if (dataPollCounter === 0) {
        setInitialPageLoading(true);
      }
      return true;
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/asset/${assetId}`,
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        setName(responseBody.assetName);
        setNickname(responseBody.assetNickname);
        setProduct(responseBody.productModel);
        setProductIdentifier(responseBody.productIdentifier);
        setProductManufacturer(responseBody.productManufacturer);
        setIsRented(responseBody.isRented);
        setIsInNetwork(responseBody.isInNetwork);
        setControllerModel(responseBody.controllerModel);
        setDeviceId(responseBody.deviceId);
        setDeviceType(responseBody.deviceModel);
        setDeviceTypeName(responseBody.deviceTypeName);
        setDeviceIdentifier(responseBody.deviceIdentifier);
        setProductType(responseBody.productType);
        setAttributeMap(responseBody.attributesById);
        const attributeIds = getAttributeIdsFromAttributeMap(responseBody.attributesById);
        const attributes = getAttributesByIds(attributeIds, responseBody.attributesById);
        markStaleAttributesInPlace(attributes);
        setAttributes(sortAttributes(attributes));
        setHighlightAttributes(getAttributesByIds(responseBody.attributeHighlightIds, responseBody.attributesById));
        setLastComm(responseBody.lastComm);
        setIsMigrating(responseBody.isMigrating);
        const cards = addAttributesToCards(responseBody.cards, responseBody.attributesById);
        setCards(cards);
        setLastConfigAttemptUtc(responseBody.lastConfigAttemptUtc);
        if (responseBody.lastUpdateFirmwareAttemptUtc !== undefined) {
          setLastUpdateFirmwareAttemptUtc(responseBody.lastUpdateFirmwareAttemptUtc);
        }

        // Due to this API endpoint being continuously polled, this logic may be reached before the configuration or
        // firmware update is complete.
        // We want to avoid overwriting the previous configuration or firmware status if it is in progress so that
        // success and failure banners stay on the page if the configuration or firmware update is applied while the
        // user is on the page.
        // If we do not do this, any success or failure banners that display to the user in response to applying
        // configuration or updating firmware will disappear the next time data is polled.
        if (previousConfigurationStatus !== "IN_PROGRESS") {
          setPreviousConfigurationStatus(configurationStatus);
        }
        if (responseBody.configurationStatus !== undefined && responseBody.configurationStatus !== null) {
          setConfigurationStatus(responseBody.configurationStatus);
        }

        if (previousFirmwareStatus !== "IN_PROGRESS") {
          setPreviousFirmwareStatus(firmwareStatus);
        }
        if (responseBody.firmwareStatus !== undefined && responseBody.firmwareStatus !== null) {
          setFirmwareStatus(responseBody.firmwareStatus);
        }

        setOnLatestFirmware(responseBody.onLatestFirmware);
        setFailedToLoad(false);
      } else {
        setFailedToLoad(true);
      }
      setInitialPageLoading(false);
    },
    [JSON.stringify(currentUser), assetId, migrationCompleted, dataPollCounter]
  );

  // Get all assets that are in the current asset's network, if it's in one.
  useApi(
    () => {
      if (isInNetwork) {
        setLoading(true);
        return true;
      } else {
        setLoading(false);
        return false;
      }
    },
    {
      url: `${API}/company/${currentUser.companyId}/asset/${assetId}/network`,
      method: "GET",
    },
    async (response: Response, responseBody: NetworkAssetResponseBody) => {
      if (response.ok && responseBody) {
        setNetworkAssets(responseBody.networkAssets);
        setFailedToLoad(false);
      } else {
        setFailedToLoad(true);
      }
      setLoading(false);
    },
    [assetId, isInNetwork, currentUser.companyId]
  );

  // Periodically poll data to give users a live feed.
  useEffect(() => {
    const newTimerId = setTimeout(() => {
      setTimerCounter((prev) => prev + 1);
      // Only poll data if the user is active.
      console.log(
        `Looking to see if data should be polled` +
          ` (inactive for ${userInactiveSeconds} seconds of ${ASSET_DETAILS_MAX_INACTIVE_USER_SECONDS} second limit).`
      );
      if (userInactiveSeconds < ASSET_DETAILS_MAX_INACTIVE_USER_SECONDS) {
        setDataPollCounter((prev) => prev + 1);
      } else {
        console.log("User is inactive. Data will not be polled.");
      }
    }, ASSET_DETAILS_POLLING_DELAY_SECONDS * MS_PER_SECOND);
    return () => {
      clearTimeout(newTimerId);
    };
  }, [timerCounter]);

  // Sort attributes alphabetically.
  function sortAttributes(attributes: Attribute[]): Attribute[] {
    return attributes.sort((a, b) => {
      if (a.attributeName < b.attributeName) {
        return -1;
      } else if (a.attributeName > b.attributeName) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  // Get an array of attributes by passing in an array of attribute IDs.
  function getAttributesByIds(attributeIds: number[], attributeMap: AttributesById): Attribute[] {
    const attributes = attributeIds.map((attributeId) => attributeMap[attributeId]);
    // If any attributes have connected attributes, get their information.
    attributes.forEach((attribute) => {
      if (attribute.connectedRegAttributeId === null || attributeMap[attribute.connectedRegAttributeId] === undefined) {
        attribute.connectedAttribute = null;
      } else {
        attribute.connectedAttribute = attributeMap[attribute.connectedRegAttributeId];
      }
    });
    return attributes;
  }

  // Get all valid attribute IDs from attribute map.
  function getAttributeIdsFromAttributeMap(attributeMap: AttributesById): number[] {
    const keys = Object.keys(attributeMap);
    const attributeIds: number[] = [];
    keys.forEach((key) => {
      const keyIntegerValue = parseInt(key, 10);
      if (!isNaN(keyIntegerValue)) {
        attributeIds.push(keyIntegerValue);
      }
    });
    return attributeIds;
  }

  // Updates the migration state based on if the asset has successfully completed migration or not.
  function handleMigrateAsset(status: Status, previousStatus: Status): void {
    if (status === "SUCCEEDED" && previousStatus === "IN_PROGRESS") {
      // Consider the migration completed if a migration was in progress and is now successful.
      setMigrationCompleted(true);
      setIsMigrating(false);
    } else if (status === "IN_PROGRESS") {
      // Consider the migration incomplete if a migration is still in progress.
      setMigrationCompleted(false);
      setIsMigrating(true);
    }
  }

  // Updates the configuration status based on if migrating an asset or applying the configuration successfully started.
  function handleApplyConfiguration(status: Status): void {
    setLastConfigAttemptUtc(null);
    setPreviousConfigurationStatus(configurationStatus);
    setConfigurationStatus(status);
  }

  // Updates the firmware status based on if updating a device's firmware successfully started.
  function handleUpdateFirmware(status: Status): void {
    setLastUpdateFirmwareAttemptUtc(null);
    setPreviousFirmwareStatus(firmwareStatus);
    setFirmwareStatus(status);
  }

  // Converts an array of cards that contains attribute IDs to have an array of cards that contain attributes.
  function addAttributesToCards(cards: AssetCard[], attributeMap: AttributesById): AssetAttributeCard[] {
    const assetAttributeCards: AssetAttributeCard[] = [];
    cards.forEach((card) => {
      const attributes = getAttributesByIds(card.attributeIds, attributeMap);
      markStaleAttributesInPlace(attributes);
      assetAttributeCards.push({
        assetTemplateCardId: card.assetTemplateCardId,
        cardTypeName: card.cardTypeName,
        name: card.name,
        attributes: attributes,
        graph: card.graph,
      });
    });
    return assetAttributeCards;
  }

  // Modifies an array of attributes to set their status on whether the current data is stale or not.
  function markStaleAttributesInPlace(attributes: Attribute[]): void {
    let engineRunningIsStale = false;
    let rpmIsStale = false;
    const nowDate = new Date();

    // Mark attributes as stale if they haven't reported a new reading in a certain amount of time.
    attributes.forEach((attribute) => {
      attribute.isStale = attribute.currentValue === null || attribute.currentValueUtc === null;
      if (!attribute.isStale) {
        const attributeDate = isoToLocalDate(attribute.currentValueUtc as string);
        attribute.isStale =
          (nowDate.getTime() - attributeDate.getTime()) / MS_PER_MINUTE > MAX_MINUTES_FOR_ACCURATE_READING;
      }
      if (attribute.isStale && attribute.attributeCode === ENGINE_RUNNING_ATTRIBUTE) {
        engineRunningIsStale = true;
      }
      if (attribute.isStale && attribute.attributeCode === RPM_ATTRIBUTE) {
        rpmIsStale = true;
      }
    });

    // Some attributes values indicate that other attributes may not have a reliable value.
    // In these cases we will mark attributes as stale if they cannot be trusted.
    const powerToPanelAttribute = attributes.find((attribute) => attribute.attributeCode === POWER_TO_PANEL_ATTRIBUTE);
    const engineRunningAttribute = attributes.find((attribute) => attribute.attributeCode === ENGINE_RUNNING_ATTRIBUTE);
    const rpmAttribute = attributes.find((attribute) => attribute.attributeCode === RPM_ATTRIBUTE);

    if (
      !engineRunningIsStale &&
      powerToPanelAttribute !== undefined &&
      engineRunningAttribute !== undefined &&
      isValueBooleanString(powerToPanelAttribute.currentValue) &&
      (powerToPanelAttribute.currentValue === null || powerToPanelAttribute.currentValue.toLowerCase() === "false")
    ) {
      engineRunningAttribute.isStale = true;
    }

    if (
      !rpmIsStale &&
      rpmAttribute !== undefined &&
      ((powerToPanelAttribute !== undefined &&
        isValueBooleanString(powerToPanelAttribute.currentValue) &&
        (powerToPanelAttribute.currentValue === null ||
          powerToPanelAttribute.currentValue.toLowerCase() === "false")) ||
        (engineRunningAttribute !== undefined &&
          isValueBooleanString(engineRunningAttribute.currentValue) &&
          (engineRunningAttribute.currentValue === null ||
            engineRunningAttribute.currentValue.toLowerCase() === "false")))
    ) {
      rpmAttribute.isStale = true;
    }
  }

  // Select an attribute so that we an open the historical modal and show the historical attribute data.
  function handleSelectedAttribute(attributeId: number, attributeMap: AttributesById | null): void {
    if (attributeMap === null) {
      setSelectedHistoryAttributeId(null);
      setSelectedHistoryStartingDate(null);
    } else {
      const selectedAttribute = attributeMap[attributeId];
      if (selectedAttribute !== undefined) {
        setSelectedHistoryAttributeId(selectedAttribute.regAttributeId);
        // We will want to get the most recent reading date, without the time of day, in the local time zone
        // to act as a default starting date for the history modal.
        // We also should apply any additional offset that is configured.
        if (typeof selectedAttribute.currentValueUtc === "string") {
          const offsetDate = isoAddDays(selectedAttribute.currentValueUtc, DATA_STARTING_DATE_OFFSET);
          const mostRecentLocal = isoUtcToIsoLocal(offsetDate);
          const mostRecentDateTimeSections = mostRecentLocal.split("T");
          setSelectedHistoryStartingDate(mostRecentDateTimeSections[0]);
        } else {
          setSelectedHistoryAttributeId(null);
          setSelectedHistoryStartingDate(null);
        }
      }
    }
  }

  // Get an attribute by ID.
  function getAttributeById(attributeId: number, attributeMap: AttributesById | null): Attribute | undefined {
    if (attributeMap === null) {
      return undefined;
    } else {
      return attributeMap[attributeId];
    }
  }

  return failedToLoad ? (
    <Error500Page />
  ) : (
    <div>
      <Spinner loading={loading || initialPageLoading} />

      <div className="pt-1" />

      {/* Shows a product specific image with attribute highlights. */}
      <ProductDataVisual
        assetName={name}
        productType={productType}
        highlightAttributes={highlightAttributes}
        allAttributes={attributes}
        onSelectAttribute={(attributeId) => handleSelectedAttribute(attributeId, attributeMap)}
        onEdit={() => setDataPollCounter(0)}
      />

      {/* Banner information, such as last reported time. */}
      <div className="row justify-content-center mx-0 mb-3">
        <div className="col-12 col-xl-8 px-3">
          <AssetStatusBannerContainer
            loading={loading || initialPageLoading}
            assetId={assetId ?? ""}
            deviceId={deviceId}
            firmwareStatus={firmwareStatus}
            configurationStatus={configurationStatus}
            previousFirmwareStatus={previousFirmwareStatus}
            previousConfigurationStatus={previousConfigurationStatus}
            deviceIdentifier={deviceIdentifier ?? ""}
            deviceTypeName={deviceTypeName ?? ""}
            isMigrating={isMigrating}
            isRented={isRented}
            onLatestFirmware={onLatestFirmware}
            lastConfigAttemptUtc={lastConfigAttemptUtc}
            lastUpdateFirmwareAttemptUtc={lastUpdateFirmwareAttemptUtc}
            lastComm={lastComm}
            onChangeLoading={(loading) => setLoading(loading)}
            onMigrateAsset={(status, previousStatus) => handleMigrateAsset(status, previousStatus)}
            onApplyConfiguration={(status) => handleApplyConfiguration(status)}
            onUpdateFirmware={(status) => handleUpdateFirmware(status)}
          />
        </div>
      </div>

      {/* Dynamically render cards for asset. */}
      {cards.length > 0 && (
        <div className="row justify-content-center mx-0 mb-4">
          <div className="col-12 col-xl-8">
            <div className="row">
              {cards.map((card) => (
                <AssetAttributeCard
                  key={card.assetTemplateCardId}
                  assetTemplateCardId={card.assetTemplateCardId}
                  name={card.name}
                  cardTypeName={card.cardTypeName}
                  attributes={card.attributes}
                  historyGraphAxisType={card.graph === null ? null : card.graph.historyGraphAxisType}
                  historyGraphType={card.graph === null ? null : card.graph.historyGraphType}
                  numberOfDaysShown={card.graph === null ? null : card.graph.numberOfDaysShown}
                  isExtraWide={card.graph === null ? false : card.graph.isExtraWide}
                  onSelectAttribute={(attributeId) => handleSelectedAttribute(attributeId, attributeMap)}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Accordion that shows all attribute data about this asset, regardless of template. */}
      <CompleteAttributeList
        attributes={attributes}
        onSelectAttribute={(attributeId) => handleSelectedAttribute(attributeId, attributeMap)}
      />

      {/* Extra non-attribute related information about the asset. */}
      <AssetDetails
        assetId={assetId !== undefined ? parseInt(assetId, 10) : assetId}
        name={name}
        nickname={nickname}
        productType={productType}
        productModel={productModel}
        productIdentifier={productIdentifier}
        productManufacturer={productManufacturer}
        deviceId={deviceId}
        deviceType={deviceType}
        deviceTypeName={deviceTypeName}
        deviceIdentifier={deviceIdentifier}
        controllerModel={controllerModel}
        loading={loading || initialPageLoading}
        isRented={isRented}
        isInNetwork={isInNetwork}
        showAssociatedAssetgroups={true}
        allowDeviceRead={true}
        allowAssetValidate={true}
        networkAssets={networkAssets}
      />

      {/* Modal for showing historical data. */}
      <DataHistoryModal
        showModal={
          selectedHistoryAttributeId !== null &&
          getAttributeById(selectedHistoryAttributeId, attributeMap) !== undefined
        }
        isGraphable={
          selectedHistoryAttributeId !== null &&
          getAttributeById(selectedHistoryAttributeId, attributeMap) !== undefined &&
          (getAttributeById(selectedHistoryAttributeId, attributeMap) as Attribute).isGraphable === true
        }
        isBoolean={
          selectedHistoryAttributeId !== null &&
          isValueBooleanString((getAttributeById(selectedHistoryAttributeId, attributeMap) as Attribute).currentValue)
        }
        isAccumulative={
          selectedHistoryAttributeId !== null &&
          getAttributeById(selectedHistoryAttributeId, attributeMap) !== undefined &&
          (getAttributeById(selectedHistoryAttributeId, attributeMap) as Attribute).isAccumulative === true
        }
        name={
          selectedHistoryAttributeId !== null &&
          getAttributeById(selectedHistoryAttributeId, attributeMap) !== undefined
            ? (getAttributeById(selectedHistoryAttributeId, attributeMap) as Attribute).attributeName
            : ""
        }
        code={
          selectedHistoryAttributeId !== null &&
          getAttributeById(selectedHistoryAttributeId, attributeMap) !== undefined
            ? (getAttributeById(selectedHistoryAttributeId, attributeMap) as Attribute).attributeCode
            : ""
        }
        date={selectedHistoryStartingDate}
        onClose={() => setSelectedHistoryAttributeId(null)}
      />
    </div>
  );
}

interface ResponseBody {
  operationIsAllowed: boolean;
  assetId: number;
  assetName: string;
  assetNickname: string;
  productManufacturer: string;
  productType: string;
  productModel: string;
  productIdentifier: string;
  controllerModel: string | null;
  isRented: boolean;
  deviceId: number | null;
  deviceModel: string | null;
  deviceTypeName: string | null;
  deviceIdentifier: string | null;
  lastComm: string | null;
  isLockedOut: boolean;
  canUnlock: boolean;
  isMigrating: boolean;
  isInNetwork: boolean;
  onLatestFirmware: boolean | null;
  lastConfigAttemptUtc: string | null;
  lastSuccessfulConfigUtc: string | null;
  lastUpdateFirmwareAttemptUtc: string | null;
  lastUpdateFirmwareSuccessfulUtc: string | null;
  configurationStatus: Status | null;
  firmwareStatus: Status | null;
  attributesById: AttributesById;
  attributeHighlightIds: number[];
  cards: AssetCard[];
}

interface AttributesById {
  [key: string]: Attribute;
}

interface Attribute {
  regAttributeId: number;
  attributeCode: string;
  attributeName: string;
  connectedRegAttributeId: number | null;
  connectedAttribute?: Attribute | null;
  unitShortName: string | null;
  unitLongName: string | null;
  icon: string;
  currentValue: string | null;
  currentValueUtc: string | null;
  isHistorical: boolean;
  isGraphable: boolean;
  isAccumulative: boolean;
  isStale: boolean;
}

interface AssetCard {
  assetTemplateCardId: number;
  cardTypeName: string | null;
  name: string;
  attributeIds: number[];
  graph: AssetCardGraph | null;
}

interface AssetAttributeCard {
  assetTemplateCardId: number;
  cardTypeName: string | null;
  name: string;
  attributes: Attribute[];
  graph: AssetCardGraph | null;
}

interface AssetCardGraph {
  historyGraphAxisType: AxisMode | null;
  historyGraphType: DisplayMode | null;
  numberOfDaysShown: number | null;
  isExtraWide: boolean;
}

interface NetworkAssetResponseBody {
  networkAssets: NetworkAsset[];
}

interface NetworkAsset {
  assetId: number;
  assetName: string;
  isGateway: boolean;
  networkDeviceTypeModel: string;
  networkDeviceTypeName: string;
  networkDeviceIdentifier: string;
  userHasAccess: boolean;
}

type DisplayMode =
  | "STANDARD_LINE_CHART"
  | "MOVING_LINE_CHART"
  | "HOURLY_ACCUMULATION"
  | "DAILY_ACCUMULATION"
  | "WEEKLY_ACCUMULATION"
  | "MONTHLY_ACCUMULATION"
  | "CHANGE_LOGS"
  | "ALL_LOGS";

type AxisMode = "Y_AXIS_ATTRIBUTE" | "Y_AXIS_UOM";

type Status = "IN_PROGRESS" | "SUCCEEDED" | "FAILED";
